/**
 * PKCE, estado e nonce — a parte pura do Authorization Code (ADR-0006, H01).
 *
 * ── Por que PKCE não é opcional aqui ──────────────────────────────────────
 *
 * O client `cliqcrm-web` é PÚBLICO: roda no navegador, e não existe lugar para
 * guardar segredo em código que o usuário baixa. Sem PKCE, quem interceptar o
 * código de autorização — no histórico do navegador, num log de proxy, num
 * `Referer` — troca esse código por token, porque não há nada mais a provar.
 *
 * O verificador é o segredo que só quem começou o fluxo tem. O provedor recebe
 * o HASH dele na ida e o valor na volta; interceptar a ida não serve de nada,
 * porque hash não volta para o original.
 *
 * O realm exige `S256` (`pkce.code.challenge.method` em `infra/keycloak`).
 * `plain` existe no padrão e é inútil: manda o verificador na URL.
 *
 * ── Estado e nonce fazem coisas diferentes ────────────────────────────────
 *
 * `state` protege contra CSRF na volta: prova que ESTA aba foi quem começou o
 * fluxo, e não uma página maliciosa que mandou o navegador para a URL de
 * retorno com um código de outra pessoa.
 *
 * `nonce` protege o ID token: entra no pedido e sai dentro do token assinado,
 * o que prova que aquele token foi emitido para ESTE pedido, e não replicado de
 * um anterior.
 *
 * São dois valores porque protegem dois momentos. Reaproveitar um para o outro
 * funciona até o dia em que só um deles for verificado.
 */

/**
 * Bytes aleatórios em base64url.
 *
 * `crypto.getRandomValues`, nunca `Math.random`: o segundo é previsível por
 * construção, e previsível aqui significa que dá para adivinhar o verificador.
 */
function aleatorio(bytes: number): string {
  const brutos = new Uint8Array(bytes);
  crypto.getRandomValues(brutos);
  return base64url(brutos);
}

/**
 * base64url sem preenchimento, como a RFC 7636 §4.1 exige.
 *
 * O `=` do base64 comum é caractere reservado em URL e viria escapado, o que
 * mudaria o valor que o provedor compara.
 */
function base64url(bytes: Uint8Array): string {
  let binario = '';
  for (const byte of bytes) binario += String.fromCharCode(byte);
  return btoa(binario).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * O verificador: 43 a 128 caracteres, pela RFC.
 *
 * 32 bytes viram 43 caracteres em base64url — o mínimo do padrão, e já são 256
 * bits de entropia. Mais que isso não acrescenta segurança e só engorda a URL.
 */
export function gerarVerificador(): string {
  return aleatorio(32);
}

/** O desafio: SHA-256 do verificador, em base64url. */
export async function desafioDe(verificador: string): Promise<string> {
  const resumo = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verificador));
  return base64url(new Uint8Array(resumo));
}

/** Anti-CSRF da volta. Ver o cabeçalho. */
export function gerarEstado(): string {
  return aleatorio(16);
}

/** Liga o ID token a este pedido. Ver o cabeçalho. */
export function gerarNonce(): string {
  return aleatorio(16);
}

/**
 * Comparação em tempo constante.
 *
 * `a === b` sai no primeiro caractere diferente, e a diferença de tempo entre
 * "errou no primeiro" e "errou no último" é mensurável. Aqui o custo de fazer
 * certo é uma dúzia de linhas.
 */
export function iguaisEmTempoConstante(a: string, b: string): boolean {
  if (a.length !== b.length) return false;
  let diferenca = 0;
  for (let i = 0; i < a.length; i += 1) {
    diferenca |= a.charCodeAt(i) ^ b.charCodeAt(i);
  }
  return diferenca === 0;
}

/** O que precisa sobreviver ao redirecionamento até a volta. */
export interface PedidoDeAutorizacao {
  readonly verificador: string;
  readonly estado: string;
  readonly nonce: string;
  /** Para onde voltar depois do login — caminho da aplicação, nunca URL absoluta. */
  readonly destino: string;
}

export async function prepararPedido(destino: string): Promise<{
  pedido: PedidoDeAutorizacao;
  desafio: string;
}> {
  const verificador = gerarVerificador();
  return {
    pedido: {
      verificador,
      estado: gerarEstado(),
      nonce: gerarNonce(),
      destino: destinoSeguro(destino),
    },
    desafio: await desafioDe(verificador),
  };
}

/**
 * Só caminho interno volta a ser destino.
 *
 * Sem isto, `?destino=https://phishing.example` faria a nossa própria tela de
 * retorno levar o usuário recém-autenticado para fora — redirecionamento aberto
 * de manual, e com a credibilidade de ter acontecido logo depois de um login
 * legítimo.
 */
export function destinoSeguro(destino: string): string {
  if (!destino.startsWith('/') || destino.startsWith('//')) return '/';
  /*
   * E a URL é MONTADA, não só olhada pelo começo (#806): o navegador lê
   * `/\site.com` (e uma barra, um tab e `/site.com`) como outro endereço, e
   * uma checagem só de prefixo deixaria passar. Montada sobre uma origem
   * nossa, ela tem de continuar nessa origem.
   */
  const origem = 'https://cliqcrm.invalido';
  let url: URL;
  try {
    url = new URL(destino, origem);
  } catch {
    return '/';
  }
  if (url.origin !== origem) return '/';
  return `${url.pathname}${url.search}${url.hash}`;
}
